import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import { img } from "@/lib/images";

const sections = [
  {
    key: "villa_location_villa_intro",
    img: img("villas/villa-location/villa-location-villakompiang-01.jpg"),
    imgAlt: "Villa Kompiang Pavilion im Garten",
    imgPos: "right" as const,
  },
  {
    key: "villa_location_taman",
    img: img("villas/villa-location/villa-location-tamanayun-01.jpg"),
    imgAlt: "Taman Ayun Fürstentempel in Mengwi",
    imgPos: "left" as const,
  },
  {
    key: "villa_location_ogoh",
    img: img("villas/villa-location/villa-location-ogogogoh-01.jpg"),
    imgAlt: "Ogoh Ogoh Museum in Mengwi",
    imgPos: "right" as const,
  },
  {
    key: "villa_location_market",
    img: img("villas/villa-location/villa-location-localmarket-01.jpg"),
    imgAlt: "Marktfrau in Mengwi",
    imgPos: "left" as const,
  },
  {
    key: "villa_location_fitness",
    img: img("villas/villa-location/villa-location-fitness-01.jpg"),
    imgAlt: "Gladiator Fitness in Gulingan Mengwi",
    imgPos: "right" as const,
  },
  {
    key: "villa_location_monkey",
    img: img("villas/villa-location/villa-location-monkey-01.jpg"),
    imgAlt: "Affe im Affenwald von Sangeh",
    imgPos: "left" as const,
  },
  {
    key: "villa_location_ubud",
    img: img("villas/villa-location/villa-location-painting-01.jpg"),
    imgAlt: "Bali Malerei in Ubud",
    imgPos: "left" as const,
  },
  {
    key: "villa_location_tanahlot",
    img: img("villas/villa-location/villa-location-tanahlot-01.jpg"),
    imgAlt: "Tanah Lot Meerestempel",
    imgPos: "left" as const,
  },
  {
    key: "villa_location_bedugul",
    img: img("villas/villa-location/villa-location-danauberatan-01.jpg"),
    imgAlt: "Ulun Danu Tempel bei Bedugul",
    imgPos: "left" as const,
  },
];

const highlights = [
  { icon: "ri-plant-line", titleKey: "villa_location_hl_rice_title", distKey: "villa_location_hl_rice_dist" },
  { icon: "ri-ancient-gate-line", titleKey: "villa_location_hl_taman_title", distKey: "villa_location_hl_taman_dist" },
  { icon: "ri-home-heart-line", titleKey: "villa_location_hl_village_title", distKey: "villa_location_hl_village_dist" },
  { icon: "ri-store-2-line", titleKey: "villa_location_hl_market_title", distKey: "villa_location_hl_market_dist" },
  { icon: "ri-palette-line", titleKey: "villa_location_hl_ubud_title", distKey: "villa_location_hl_ubud_dist" },
  { icon: "ri-water-flash-line", titleKey: "villa_location_hl_tanahlot_title", distKey: "villa_location_hl_tanahlot_dist" },
];

export default function VillaLocationPage() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={isDe ? "Villen" : "Villas"}
          title={t("villa_location_title")}
          subtitle={t("villa_location_intro")}
          image={img("villas/villa-location/header-villa-location-01.jpg")}
          imageAlt="Villa Kompiang Lage und Umgebung"
        />

        {/* Highlights */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8 text-center">
              {t("villa_location_highlights_title")}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {highlights.map((h) => (
                <div
                  key={h.icon}
                  className="bg-background-100 border border-background-200 rounded-lg p-5 flex flex-col items-center text-center"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-3">
                    <i className={`${h.icon} text-xl`}></i>
                  </span>
                  <span className="font-medium text-foreground-900 text-sm leading-snug">
                    {t(h.titleKey)}
                  </span>
                  <span className="text-xs text-foreground-600 mt-1">{t(h.distKey)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="rounded-lg overflow-hidden border border-background-200 h-80 md:h-96">
              <iframe
                src="https://www.google.com/maps?q=Jalan%20Ganetri%209%20D%2C%20Gatot%20Subroto%20Timur%2C%20Denpasar%2080235%2C%20Bali%2C%20Indonesia&z=17&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Villa Kompiang Lage"
              />
            </div>
          </div>
        </section>

        {/* Content + Sidebar */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-8 space-y-8">
                {sections.map((s) => (
                  <article key={s.key} className="scroll-mt-24">
                    <div className={`flex flex-col ${s.imgPos === "left" ? "md:flex-row" : "md:flex-row-reverse"} gap-5 md:gap-6 items-start`}>
                      <div className="w-full md:w-96 flex-shrink-0">
                        <div className="rounded-lg overflow-hidden border border-background-200">
                          <img
                            src={s.img}
                            alt={s.imgAlt}
                            className="w-full h-64 md:h-[420px] object-cover object-center"
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">
                          {t(`${s.key}_title`)}
                        </h2>
                        <div className="text-sm text-foreground-700 leading-relaxed space-y-3">
                          {t(`${s.key}_text`).split("\n").map((p, i) => (
                            <p key={i}>{p}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-4 space-y-6">
                {/* Villa Links */}
                <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                  <h3 className="font-heading text-sm font-semibold text-accent-800 uppercase tracking-wider mb-3 pb-2 border-b border-accent-200">
                    {t("villa_info_sidebar_villas")}
                  </h3>
                  <nav className="space-y-1">
                    {[
                      { to: "/villa-frangipani", label: "villas_room_1_title" },
                      { to: "/villa-bougainvillea", label: "villas_room_2_title" },
                      { to: "/villa-hibiscus", label: "villas_room_3_title" },
                      { to: "/villas", label: "villas_gallery_title" },
                    ].map((l) => (
                      <LocalizedLink
                        key={l.to}
                        to={l.to}
                        className="block text-sm text-foreground-700 hover:text-accent-700 hover:bg-accent-100 px-3 py-2 rounded-md transition-colors cursor-pointer"
                      >
                        {t(l.label)}
                      </LocalizedLink>
                    ))}
                  </nav>
                </div>

                {/* Wedding Links */}
                <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                  <h3 className="font-heading text-sm font-semibold text-accent-800 uppercase tracking-wider mb-3 pb-2 border-b border-accent-200">
                    {t("villa_info_sidebar_weddings")}
                  </h3>
                  <nav className="space-y-1">
                    {[
                      { to: "/weddings/general-wedding-information", label: "wedding_cat_allgemein_title" },
                      { to: "/weddings", label: "wedding_cat_galerie_title" },
                    ].map((l) => (
                      <LocalizedLink
                        key={l.to}
                        to={l.to}
                        className="block text-sm text-foreground-700 hover:text-accent-700 hover:bg-accent-100 px-3 py-2 rounded-md transition-colors cursor-pointer"
                      >
                        {t(l.label)}
                      </LocalizedLink>
                    ))}
                  </nav>
                </div>

                {/* Tour Links */}
                <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                  <h3 className="font-heading text-sm font-semibold text-accent-800 uppercase tracking-wider mb-3 pb-2 border-b border-accent-200">
                    {t("villa_info_sidebar_tours")}
                  </h3>
                  <nav className="space-y-1">
                    {[
                      { to: "/day-trips", label: "tour_category_daytrips_title" },
                      { to: "/multi-day-tours", label: "tour_category_multiday_title" },
                      { to: "/adventure-tours", label: "tour_category_adventure_title" },
                      { to: "/activities", label: "wedding_cat_galerie_title" },
                    ].map((l) => (
                      <LocalizedLink
                        key={l.to}
                        to={l.to}
                        className="block text-sm text-foreground-700 hover:text-accent-700 hover:bg-accent-100 px-3 py-2 rounded-md transition-colors cursor-pointer"
                      >
                        {t(l.label)}
                      </LocalizedLink>
                    ))}
                  </nav>
                </div>

                {/* Contact */}
                <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                  <h3 className="font-heading text-sm font-semibold text-accent-800 uppercase tracking-wider mb-3 pb-2 border-b border-accent-200">
                    {t("villa_info_sidebar_contact")}
                  </h3>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    {t("villa_info_sidebar_contact_note")}
                  </p>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </span>
                      <div>
                        <p className="font-medium text-foreground-800">{isDe ? "Telefon & WhatsApp:" : "Phone & WhatsApp:"}</p>
                        <p className="text-foreground-600">+62 361 8942606</p>
                        <p className="text-foreground-600">+62 81 246 21 8000</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </span>
                      <div>
                        <p className="font-medium text-foreground-800">E-Mail:</p>
                        <p className="text-foreground-600">office@balivwtour.com</p>
                      </div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}